/**
 * Navigation-section visibility. Independent of permission bitmasks:
 *
 *   - permissions  -> WHAT a user can do once a section is reached
 *   - visibility   -> WHETHER a section is offered to a company at all
 *
 * A child company's effective visibility is clamped by its parent's
 * (waterfall). If a parent hides a section, no descendant may surface it.
 * If a parent puts a section in "marketing", a descendant may further
 * downgrade it to "hidden" but not upgrade it to "enabled".
 *
 * Tri-state per section:
 *   - 'enabled'    -> nav link shown, route renders the real page (subject to permission bits)
 *   - 'marketing'  -> nav link shown with a teaser badge, route renders an upsell/marketing page
 *   - 'hidden'     -> nav link removed entirely, route blocked
 */

export const navSectionKeys = [
  'overview',
  // Organisation group
  'organisation',
  'organisation.overview',
  'organisation.businessServices',
  'organisation.businessFunctions',
  'organisation.enablingServices',
  'organisation.assets',
  'organisation.users',
  'organisation.customers',
  'organisation.suppliers',
  'organisation.interestedParties',
  // Governance group
  'governance',
  'governance.goals',
  'governance.securityProjects',
  'governance.kpis',
  'governance.rasci',
  'governance.accessMatrix',
  'governance.complianceCalendar',
  'governance.assetCategoryClassification',
  'governance.exceptions',
  // Risk group
  'risk',
  'risk.bia',
  'risk.risks',
  'risk.threats',
  'risk.vulnerabilities',
  'risk.measures',
  // Incidents group
  'incidents',
  'incidents.incidents',
  'incidents.scenarios',
  'incidents.playbooks',
  'incidents.problems',
  // Disaster Recovery group
  'disasterRecovery',
  'disasterRecovery.tests',
  'disasterRecovery.scenarios',
  'disasterRecovery.checklists',
  // Compliance group
  'compliance',
  'compliance.gapAnalysis',
  'compliance.soa',
  'compliance.assessments',
  'compliance.systemKpis',
  'compliance.policyLibrary',
  'compliance.frameworkUcf',
  'compliance.frameworkDora',
  'compliance.frameworkGdpr',
  'compliance.frameworkPciDss',
  'compliance.frameworkLaw95',
  'compliance.frameworkLaw69',
  'compliance.frameworkIso27001',
  // Time tracking group
  'timetracking',
  'timetracking.myTimesheet',
  'timetracking.holidays',
  'timetracking.reports',
  'timetracking.exports',
  'timetracking.projects',
  // Training group
  'training',
  'training.videos',
  'training.campaigns',
  // Marketplace group
  'marketplace',
  'marketplace.gen360',
  // Settings group — covers entries the admin can toggle per company.
  // Personal-account / security / theme / language are always available
  // and intentionally omitted.
  'settings',
  'settings.organisation',
  'settings.tree',
  'settings.functions',
  'settings.emailTemplates',
  'settings.idNumbering',
  'settings.branding',
  'settings.connectors',
  'settings.sensors',
  'settings.importExport',
  'settings.accessValidation',
  'settings.team',
  'settings.invites',
  'settings.sso',
  'settings.billing'
] as const;

export type NavSectionKey = (typeof navSectionKeys)[number];

export const sectionVisibilityValues = ['hidden', 'marketing', 'enabled'] as const;
export type SectionVisibility = (typeof sectionVisibilityValues)[number];

export type OrganisationSectionVisibility = Partial<Record<NavSectionKey, SectionVisibility>>;

const visibilityRank: Record<SectionVisibility, number> = {
  hidden: 0,
  marketing: 1,
  enabled: 2
};

/**
 * Default visibility when a section has no explicit setting. A new
 * organisation starts with every section HIDDEN — the admin app is the
 * sole place where sections are opted into 'marketing' or 'enabled'.
 *
 * This was chosen over "default enabled" so that:
 *   - Spinning up a new tenant doesn't accidentally surface a feature
 *     the customer hasn't bought.
 *   - The admin UI is the single source of truth for what a company
 *     can see, which keeps the surface area visible to support.
 *
 * Root organisations get their own treatment via
 * `getRootOrganisationSectionVisibility`: sections in
 * `rootAlwaysVisibleSections` fall back to 'enabled' so the
 * administrative shell stays usable, everything else is hidden.
 */
export const defaultSectionVisibility: SectionVisibility = 'hidden';

/**
 * The fixed nav surface for the root organisation. Root is an
 * administrative shell — it manages the system, it doesn't run the
 * product — so its nav stays tiny regardless of how broad the account's
 * permissions / stored visibility get. Adding a section here is the
 * only way to surface it in the root user's nav; the admin permissions
 * UI cannot widen this set.
 *
 * Why these specific sections:
 *   - `overview` — the dashboard / landing page.
 *   - `settings` + `settings.organisation` / `settings.tree` — managing
 *     the root org itself + the corporate tree of children.
 *   - `settings.idNumbering` / `settings.branding` — system-wide
 *     configuration that has nowhere else to live.
 *   - `settings.connectors` / `settings.sensors` — infrastructure-style
 *     integrations the operator manages centrally.
 *   - `settings.team` — managing system administrators.
 *
 * Intentionally NOT here (even when the account holds permissions for
 * them, they're for tenant orgs only): `settings.functions`,
 * `settings.emailTemplates`, `settings.invites`, `settings.sso`,
 * `settings.accessValidation`, `settings.importExport`,
 * `settings.billing`, plus every product surface (organisation tree
 * children, governance, risk, compliance, training, marketplace).
 */
export const rootAlwaysVisibleSections: ReadonlySet<NavSectionKey> = new Set<NavSectionKey>([
  'overview',
  'settings',
  'settings.organisation',
  'settings.tree',
  'settings.idNumbering',
  'settings.branding',
  'settings.connectors',
  'settings.sensors',
  'settings.team'
]);

/**
 * Root organisations only expose a thin slice of the navigation regardless
 * of their stored permissions/visibility — they are administrative shells
 * that don't render the full product surface. Anything outside the
 * whitelist is reported as hidden.
 */
export function getRootOrganisationSectionVisibility(
  stored: OrganisationSectionVisibility
): OrganisationSectionVisibility {
  const out: OrganisationSectionVisibility = {};
  for (const key of navSectionKeys) {
    if (!rootAlwaysVisibleSections.has(key)) {
      out[key] = 'hidden';
      continue;
    }
    // Sections in the root whitelist are always available unless the
    // stored map explicitly downgrades them. We hardcode the fallback
    // to 'enabled' here (rather than `defaultSectionVisibility`) so
    // that flipping the global default to 'hidden' doesn't accidentally
    // blank out the administrative shell.
    out[key] = stored[key] ?? 'enabled';
  }
  return out;
}

/**
 * Take the minimum of two visibilities (more restrictive wins). Used to
 * clamp a child organisation against its parent waterfall.
 */
export function clampVisibility(
  child: SectionVisibility,
  parent: SectionVisibility
): SectionVisibility {
  return visibilityRank[child] <= visibilityRank[parent] ? child : parent;
}

/**
 * Combine a chain of organisation visibility maps (root-first → leaf-last)
 * into a single resolved map. Each child is clamped down by every ancestor.
 * Missing entries fall back to `defaultSectionVisibility`.
 */
export function getResolvedSectionVisibility(
  chain: readonly OrganisationSectionVisibility[]
): Record<NavSectionKey, SectionVisibility> {
  const resolved = {} as Record<NavSectionKey, SectionVisibility>;
  for (const key of navSectionKeys) {
    // Start at the most permissive value and let each ancestor (and
    // finally the org itself) clamp it down. Missing entries still
    // fall back to `defaultSectionVisibility` per-entry, so an empty
    // map keeps the "default off" semantics.
    //
    // (Starting at `defaultSectionVisibility = 'hidden'` would clamp
    // every walk to 'hidden' immediately — `clampVisibility` always
    // returns the more restrictive of the two — so marketing /
    // enabled values stored on the chain would never survive.)
    let current: SectionVisibility = 'enabled';
    for (const entry of chain) {
      const next = entry[key] ?? defaultSectionVisibility;
      current = clampVisibility(next, current);
    }
    resolved[key] = current;
  }
  return resolved;
}

/**
 * Parse the JSON blob stored on Organisation.sectionVisibility into a typed
 * map, dropping unknown keys and invalid values.
 */
export function parseSectionVisibility(raw: unknown): OrganisationSectionVisibility {
  if (raw == null || typeof raw !== 'object') return {};
  const known = new Set<NavSectionKey>(navSectionKeys);
  const validValues = new Set<SectionVisibility>(sectionVisibilityValues);
  const out: OrganisationSectionVisibility = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!known.has(key as NavSectionKey)) continue;
    if (typeof value !== 'string' || !validValues.has(value as SectionVisibility)) continue;
    out[key as NavSectionKey] = value as SectionVisibility;
  }
  return out;
}

/**
 * Serialise a visibility map to an array shape suitable for GraphQL.
 */
export function serialiseSectionVisibility(
  map: OrganisationSectionVisibility
): Array<{ section: NavSectionKey; visibility: SectionVisibility }> {
  return Object.entries(map)
    .filter(([key]) => (navSectionKeys as readonly string[]).includes(key))
    .map(([section, visibility]) => ({
      section: section as NavSectionKey,
      visibility: visibility as SectionVisibility
    }));
}

/**
 * Build a visibility map from the array shape returned by GraphQL.
 */
export function deserialiseSectionVisibility(
  list: ReadonlyArray<{ section: string; visibility: string }>
): OrganisationSectionVisibility {
  return parseSectionVisibility(
    list.reduce<Record<string, string>>((acc, item) => {
      acc[item.section] = item.visibility;
      return acc;
    }, {})
  );
}

/**
 * Apply a partial patch to an existing visibility map, dropping entries
 * that fall back to the default (keeps the stored JSON compact).
 */
export function setSectionVisibility(
  current: OrganisationSectionVisibility,
  patch: OrganisationSectionVisibility
): OrganisationSectionVisibility {
  const next: OrganisationSectionVisibility = { ...current };
  for (const [key, value] of Object.entries(patch) as Array<[NavSectionKey, SectionVisibility]>) {
    if (value === defaultSectionVisibility) {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return next;
}

/**
 * Maps the typed NavSection route literal used in the navigation bar to
 * its section key. Anything not listed here is treated as "always
 * visible" (e.g. /settings/personal-account).
 */
export const navRouteToSectionKey: Record<string, NavSectionKey> = {
  '/': 'overview',
  // Organisation group
  '/organisation': 'organisation.overview',
  '/business-services': 'organisation.businessServices',
  '/business-functions': 'organisation.businessFunctions',
  '/enabling-services': 'organisation.enablingServices',
  '/assets': 'organisation.assets',
  '/users': 'organisation.users',
  '/customers': 'organisation.customers',
  '/suppliers': 'organisation.suppliers',
  '/interested-parties': 'organisation.interestedParties',
  // Governance
  '/goals': 'governance.goals',
  '/security-projects': 'governance.securityProjects',
  '/kpis': 'governance.kpis',
  '/rasci': 'governance.rasci',
  '/access-matrix': 'governance.accessMatrix',
  '/compliance-calendar': 'governance.complianceCalendar',
  '/asset-category-classification': 'governance.assetCategoryClassification',
  '/exceptions': 'governance.exceptions',
  // Risk
  '/bia': 'risk.bia',
  '/risks': 'risk.risks',
  '/threats': 'risk.threats',
  '/vulnerabilities': 'risk.vulnerabilities',
  '/measures': 'risk.measures',
  // Incidents
  '/incidents': 'incidents.incidents',
  '/scenarios': 'incidents.scenarios',
  '/playbooks': 'incidents.playbooks',
  '/problems': 'incidents.problems',
  // Disaster Recovery
  '/disaster-recovery/tests': 'disasterRecovery.tests',
  '/disaster-recovery/scenarios': 'disasterRecovery.scenarios',
  '/disaster-recovery/checklists': 'disasterRecovery.checklists',
  // Compliance
  '/compliance': 'compliance.gapAnalysis',
  '/soa': 'compliance.soa',
  '/assessments': 'compliance.assessments',
  '/compliance/all/kpis': 'compliance.systemKpis',
  '/policies/search': 'compliance.policyLibrary',
  '/framework/ucf': 'compliance.frameworkUcf',
  '/framework/dora': 'compliance.frameworkDora',
  '/framework/gdpr': 'compliance.frameworkGdpr',
  '/framework/pci-dss': 'compliance.frameworkPciDss',
  // Bare `/framework/<id>` routes for the laws + ISO exist alongside
  // the customer-facing `/assurance/...` aliases — both must map to
  // the same section key so direct URL navigation hits the gate.
  '/framework/iso-27001': 'compliance.frameworkIso27001',
  '/framework/69-2018': 'compliance.frameworkLaw69',
  '/framework/95-2019': 'compliance.frameworkLaw95',
  '/assurance/95-2019': 'compliance.frameworkLaw95',
  '/assurance/69-2018': 'compliance.frameworkLaw69',
  '/assurance/iso-27001-2022': 'compliance.frameworkIso27001',
  // Time tracking
  '/timetracking/my-timesheet': 'timetracking.myTimesheet',
  '/timetracking/holidays': 'timetracking.holidays',
  '/timetracking/reports': 'timetracking.reports',
  '/timetracking/exports': 'timetracking.exports',
  '/timetracking/projects': 'timetracking.projects',
  // Training
  '/video': 'training.videos',
  '/campaign': 'training.campaigns',
  // Marketplace
  '/marketplace': 'marketplace',
  '/marketplace/gen360': 'marketplace.gen360',
  // Settings
  '/settings/organisation': 'settings.organisation',
  '/settings/tree': 'settings.tree',
  '/settings/functions': 'settings.functions',
  '/settings/email-templates': 'settings.emailTemplates',
  '/settings/id-numbering': 'settings.idNumbering',
  '/settings/branding': 'settings.branding',
  '/settings/connectors': 'settings.connectors',
  '/settings/sensors': 'settings.sensors',
  '/settings/import-export': 'settings.importExport',
  '/settings/access-validation': 'settings.accessValidation',
  '/settings/team': 'settings.team',
  '/settings/invites': 'settings.invites',
  '/settings/sso': 'settings.sso',
  '/settings/billing': 'settings.billing'
};

/**
 * Inverse of `navRouteToSectionKey`, useful for showing the section path
 * in admin UIs and for the marketing page picking the right copy.
 */
export const navSectionKeyToRoute: Partial<Record<NavSectionKey, string>> = Object.entries(
  navRouteToSectionKey
).reduce<Partial<Record<NavSectionKey, string>>>((acc, [route, key]) => {
  if (acc[key] == null) acc[key] = route;
  return acc;
}, {});

/**
 * Parent group key for a given section. Used so the admin UI can render
 * sections grouped by their navigation parent, and so the resolver can
 * propagate a parent's visibility down to its children (a "hidden"
 * parent implies hidden children regardless of their own setting).
 */
export const navSectionParent: Record<NavSectionKey, NavSectionKey | null> = {
  overview: null,
  organisation: null,
  'organisation.overview': 'organisation',
  'organisation.businessServices': 'organisation',
  'organisation.businessFunctions': 'organisation',
  'organisation.enablingServices': 'organisation',
  'organisation.assets': 'organisation',
  'organisation.users': 'organisation',
  'organisation.customers': 'organisation',
  'organisation.suppliers': 'organisation',
  'organisation.interestedParties': 'organisation',
  governance: null,
  'governance.goals': 'governance',
  'governance.securityProjects': 'governance',
  'governance.kpis': 'governance',
  'governance.rasci': 'governance',
  'governance.accessMatrix': 'governance',
  'governance.complianceCalendar': 'governance',
  'governance.assetCategoryClassification': 'governance',
  'governance.exceptions': 'governance',
  risk: null,
  'risk.bia': 'risk',
  'risk.risks': 'risk',
  'risk.threats': 'risk',
  'risk.vulnerabilities': 'risk',
  'risk.measures': 'risk',
  incidents: null,
  'incidents.incidents': 'incidents',
  'incidents.scenarios': 'incidents',
  'incidents.playbooks': 'incidents',
  'incidents.problems': 'incidents',
  disasterRecovery: null,
  'disasterRecovery.tests': 'disasterRecovery',
  'disasterRecovery.scenarios': 'disasterRecovery',
  'disasterRecovery.checklists': 'disasterRecovery',
  compliance: null,
  'compliance.gapAnalysis': 'compliance',
  'compliance.soa': 'compliance',
  'compliance.assessments': 'compliance',
  'compliance.systemKpis': 'compliance',
  'compliance.policyLibrary': 'compliance',
  'compliance.frameworkUcf': 'compliance',
  'compliance.frameworkDora': 'compliance',
  'compliance.frameworkGdpr': 'compliance',
  'compliance.frameworkPciDss': 'compliance',
  'compliance.frameworkLaw95': 'compliance',
  'compliance.frameworkLaw69': 'compliance',
  'compliance.frameworkIso27001': 'compliance',
  timetracking: null,
  'timetracking.myTimesheet': 'timetracking',
  'timetracking.holidays': 'timetracking',
  'timetracking.reports': 'timetracking',
  'timetracking.exports': 'timetracking',
  'timetracking.projects': 'timetracking',
  training: null,
  'training.videos': 'training',
  'training.campaigns': 'training',
  marketplace: null,
  'marketplace.gen360': 'marketplace',
  settings: null,
  'settings.organisation': 'settings',
  'settings.tree': 'settings',
  'settings.functions': 'settings',
  'settings.emailTemplates': 'settings',
  'settings.idNumbering': 'settings',
  'settings.branding': 'settings',
  'settings.connectors': 'settings',
  'settings.sensors': 'settings',
  'settings.importExport': 'settings',
  'settings.accessValidation': 'settings',
  'settings.team': 'settings',
  'settings.invites': 'settings',
  'settings.sso': 'settings',
  'settings.billing': 'settings'
};

/**
 * Effective visibility for a section after taking its parent group into
 * account: if the parent is hidden, the child is hidden too even if the
 * child entry says "enabled". This mirrors the navtab — a child can't
 * exist without its parent slot being rendered.
 */
export function getEffectiveSectionVisibility(
  resolved: Record<NavSectionKey, SectionVisibility>,
  key: NavSectionKey
): SectionVisibility {
  const parent = navSectionParent[key];
  if (parent == null) return resolved[key];
  return clampVisibility(resolved[key], resolved[parent]);
}
